import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, Copy, CalendarClock } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { SLOTS, dayNames } from "@/lib/schedule";
import { ModEvents } from "@/components/mod/ModEvents";

const DAYS = dayNames("hu");
const label = (s) => `${String(s * 2).padStart(2, "0")}:00–${String(s * 2 + 2).padStart(2, "0")}:00`;

function SlotEditor({ k, entries, users, onChange, onCopyDay }) {
  const [day, slot] = k.split("-").map(Number);
  const [agent, setAgent] = useState("");
  const [duty, setDuty] = useState("");
  const add = () => {
    const u = users.find((x) => x.id === agent);
    if (!u) return;
    onChange([...entries, { agent_id: u.id, name: u.name, duty }]);
    setDuty("");
  };
  const sel = "h-9 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-[13px] text-zinc-100";
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4" data-testid="schedule-editor">
      <div className="mb-3 text-[13px] font-semibold text-zinc-100">{DAYS[day]} · <span className="font-mono">{label(slot)}</span> <span className="text-[11px] font-normal text-zinc-500">CET/CEST</span></div>
      <div className="space-y-2">
        {entries.length === 0 && <p className="text-[12px] text-zinc-600">Nincs beosztott agent.</p>}
        {entries.map((e, i) => (
          <div key={i} className={`flex items-center gap-2 ${e.cancelled ? "opacity-60" : ""}`} data-testid={`schedule-entry-${i}`}>
            <span className={`w-32 truncate text-[13px] ${e.cancelled ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{e.name}</span>
            <Input value={e.duty} onChange={(ev) => onChange(entries.map((x, j) => (j === i ? { ...x, duty: ev.target.value } : x)))} placeholder="Feladat / szakterület" className="h-8 border-zinc-800 bg-zinc-950 text-[12px]" />
            <button data-testid={`schedule-entry-cancel-${i}`} onClick={() => onChange(entries.map((x, j) => (j === i ? { ...x, cancelled: !x.cancelled } : x)))}
              className={`h-8 shrink-0 rounded-md border px-2 text-[11px] ${e.cancelled ? "border-red-500/50 bg-red-500/10 text-red-300" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>{e.cancelled ? "Lemondva" : "Lemondás"}</button>
            <button data-testid={`schedule-entry-remove-${i}`} onClick={() => onChange(entries.filter((_, j) => j !== i))} className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 border-t border-zinc-800/70 pt-3">
        <select data-testid="schedule-agent-select" value={agent} onChange={(e) => setAgent(e.target.value)} className={sel}>
          <option value="">Agent kiválasztása…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <Input data-testid="schedule-duty-input" value={duty} onChange={(e) => setDuty(e.target.value)} placeholder="pl. Gyermekvédelem" className="h-9 w-48 border-zinc-800 bg-zinc-950 text-[13px]" />
        <button data-testid="schedule-add-entry" onClick={add} disabled={!agent} className="inline-flex h-9 items-center gap-1 rounded-md bg-blue-600 px-3 text-[12px] font-semibold text-white disabled:opacity-40"><Plus size={13} /> Hozzáad</button>
        <button data-testid="schedule-copy-week" onClick={() => onCopyDay(slot, entries)} className="ml-auto inline-flex h-9 items-center gap-1 rounded-md border border-zinc-800 px-3 text-[12px] text-zinc-300 hover:bg-zinc-900"><Copy size={13} /> Másolás a hét minden napjára</button>
      </div>
    </div>
  );
}

export function ModSchedule() {
  const [slots, setSlots] = useState({});
  const [users, setUsers] = useState([]);
  const [selKey, setSelKey] = useState("0-4");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/admin/schedule").then((r) => setSlots(r.data.slots || {}));
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.active !== false)));
  }, []);

  const setEntries = (k, entries) => { setSlots((s) => ({ ...s, [k]: entries })); setDirty(true); };
  const copyWeek = (slot, entries) => { setSlots((s) => { const n = { ...s }; for (let d = 0; d < 7; d++) n[`${d}-${slot}`] = entries.map((e) => ({ ...e })); return n; }); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/schedule", { slots });
      setSlots(r.data.slots);
      setDirty(false);
      toast.success("Beosztás mentve — a nyilvános oldalon azonnal látszik");
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="mod-schedule">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarClock size={18} className="text-blue-400" />
        <h2 className="text-lg font-semibold text-zinc-50">Beosztás</h2>
        <span className="text-[12px] text-zinc-500">Időzóna: CET/CEST (Budapest) · a látogatók a saját helyi idejükben látják</span>
        <a href="/availability" target="_blank" rel="noreferrer" className="text-[12px] text-blue-400 hover:underline">Nyilvános oldal</a>
        <button data-testid="schedule-save" onClick={save} disabled={saving || !dirty} className="ml-auto inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Mentés
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-zinc-800/70 bg-[#0d0d0f] p-3">
        <div className="grid min-w-[720px] gap-1" style={{ gridTemplateColumns: "92px repeat(7, minmax(0, 1fr))" }}>
          <div />
          {DAYS.map((d) => <div key={d} className="pb-1 text-center text-[11px] font-semibold capitalize text-zinc-400">{d}</div>)}
          {SLOTS.map((slot) => (
            <React.Fragment key={slot}>
              <div className="flex items-center justify-end pr-2 font-mono text-[10px] text-zinc-500">{label(slot)}</div>
              {DAYS.map((_, day) => {
                const k = `${day}-${slot}`;
                const e = slots[k] || [];
                return (
                  <button key={k} data-testid={`schedule-cell-${k}`} onClick={() => setSelKey(k)}
                    className={`h-10 truncate rounded px-1.5 text-left text-[10px] transition-colors ${e.length && e.every((x) => x.cancelled) ? "border border-red-500/40 bg-red-950/30 text-red-300 line-through" : e.length ? "bg-blue-600/80 text-white hover:bg-blue-500" : "border border-zinc-800/60 text-zinc-600 hover:border-zinc-600"} ${selKey === k ? "ring-2 ring-[#FFCC00]" : ""}`}>
                    {e.map((x) => x.name).join(", ")}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <SlotEditor key={selKey} k={selKey} entries={slots[selKey] || []} users={users} onChange={(en) => setEntries(selKey, en)} onCopyDay={copyWeek} />
      <div className="pt-6"><ModEvents /></div>
    </div>
  );
}
